'use client'

import Link from 'next/link'
import { AdvertisingModalEmbedded } from '@/AdvertisingModal/AdvertisingModalEmbedded'
import type { AdvertisingModalContent } from '@/AdvertisingModal/types'
import { useStore } from '@/store'
import { EMAIL_REGEX } from '@/validation'
import styles from './newsletter.module.css'

const content: AdvertisingModalContent = {
  title: '',
  submitButtonText: 'Send me great magic',
  successMessage:
    'You’re on the list. Look out for the next Vanishing Inc. newsletter in your inbox. We’re glad you’re here.',
  formFields: [
    {
      name: 'name',
      label: 'Your name',
      placeholder: 'First and last name',
      required: true,
      validation: {
        pattern: /^\S+ +\S+/,
        message: 'Please enter your first and last name',
      },
    },
    {
      name: 'email',
      label: 'Email address',
      type: 'email',
      placeholder: 'you@example.com',
      required: true,
      validation: {
        pattern: EMAIL_REGEX,
        message: 'Please enter a valid email address',
      },
    },
  ],
}

export function NewsletterSignup() {
  const isSignedIn = useStore(state => Boolean(state.user))

  return (
    <div
      id="newsletter-signup"
      className={styles.signup}
      tabIndex={-1}
      role="region"
      aria-labelledby="signup-title"
    >
      <h2 id="signup-title">Make a little room for magic.</h2>
      <p className={styles.signupIntro}>
        Join our free newsletter. We’d love to have you with us.
      </p>

      {isSignedIn ? (
        <div className={styles.accountState}>
          <p>
            You’re signed in. You can check your subscription and choose the
            emails you’d like to receive in your account.
          </p>
          <Link href="/account/newsletters/" className={styles.cta}>
            Choose my emails
          </Link>
        </div>
      ) : (
        <>
          <div aria-live="polite">
            <AdvertisingModalEmbedded
              content={content}
              trackingEventName="Newsletter signup"
              trackingProperties={{
                source: 'newsletter-page',
                offer: 'Vanishing Inc. newsletter',
              }}
              config={{ layout: 'stacked', enableCaptcha: true }}
              className={styles.embedded}
              stackedContentClassName={styles.formContent}
              customInputClassName={styles.input}
              customButtonClassName={styles.submit}
              customSuccessClassName={styles.success}
              existingAccountSuccessTitle="Welcome back"
              existingAccountSuccessMessage="There’s already an account with this email. Use the sign-in link below to check your newsletter preferences."
              hideTitleInSuccess
            />
          </div>
          <p className={styles.consent}>
            By joining, you’ll receive news, learning inspiration, and offers
            from Vanishing Inc. Unsubscribe whenever you like.{' '}
            <Link href="/faqs/privacy/">Privacy policy</Link>.
          </p>
          <p className={styles.signIn}>
            Already have an account?{' '}
            <Link href="/account/login/?redirect=%2Faccount%2Fnewsletters%2F">
              Sign in to choose your emails
            </Link>
            .
          </p>
        </>
      )}
    </div>
  )
}
